import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAlert } from "./useAlert";

export interface IVoucher {
  _id?: string;
  partner?: string;
  code?: string;
  value?: string;
  type?: string;
  sourceType?: "CHALLENGE" | "LOTTERY" | "DROP" | "UNKNOWN";
  /** TEXT renders the plain code in the app; QR renders it as a scannable QR. */
  codeDisplayFormat?: "TEXT" | "QR";
  title?: string;
  description?: string;
  instructions?: string;
  /** Bilingual instructions — DROP (IKEA-style) vouchers only. */
  instructionsEn?: string;
  instructionsNl?: string;
  /** One optional address, shown at the foot of the voucher in the app. */
  link?: string;
  benefitText?: string;
  color?: string;
  pickupLocation?: string;
  startsAt?: Date;
  endsAt?: Date;
  expireDate?: Date | string | null;
  status?: "ACTIVE" | "INACTIVE" | "EXPIRED" | "REDEEMED" | "USED";
  appliesOnce?: boolean;
  partnerId?: string | Record<string, any>;
  challenge?: string | Record<string, any> | null;
  lottery?: string | Record<string, any> | null;
  /** The drop that earned this voucher (sourceType DROP). Populated with
   *  createdAt, partnerData and location on the pending-codes endpoint. */
  drop?: string | Record<string, any> | null;
  codeAssignedBy?: string | Record<string, any> | null;
  codeAssignedAt?: Date | string | null;
  redeemedBy?: string[] | any[];
  allowMultipleRedeems?: boolean;
  allowMultipleRedemptions?: boolean | string;
  isProduct?: boolean;
  prizeName?: string;
  /** Bilingual prize label — DROP (IKEA-style) vouchers only. */
  prizeNameEn?: string;
  prizeNameNl?: string;
  updatedAt?: Date;
  createdAt?: Date;
}

export interface IVoucherUser {
  _id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
}

/** Every voucher the Challenge vouchers page lists for a partner (and challenge). */
export interface IVoucherSelection {
  ids: string[];
  total: number;
  /** How many of them are already given to customers. */
  issued: number;
}

/** Voucher counts of one partner on the Challenge vouchers page. */
export interface IVoucherCounts {
  total: number;
  /** Given to customers. */
  issued: number;
  products: number;
  codes: number;
}

export interface IVoucherSummary extends IVoucherCounts {
  /** Per challenge; `challenge` is null for the partner's unpinned pool. */
  byChallenge: (IVoucherCounts & { challenge: string | null })[];
}

/** What a bulk edit may change. An empty string clears a field. */
export interface IBulkVoucherChanges {
  description?: string;
  instructions?: string;
  link?: string;
  benefitText?: string;
}

export interface IBulkVoucherResult {
  requested: number;
  notFound: number;
  skippedDrop: number;
  issued: number;
  skippedIssued: number;
  willChange: number;
  dryRun: boolean;
  changed: number;
}

export interface IBulkDeleteResult {
  requested: number;
  notFound: number;
  skippedDrop: number;
  /** Given to customers, so never deleted. */
  skippedIssued: number;
  willDelete: number;
  /** Live challenges the delete leaves with no usable voucher. */
  emptiedChallenges: string[];
  dryRun: boolean;
  deleted: number;
}

/**
 * The backend's message from a failed call. axiosConfig rejects with the
 * response itself, so the message sits on `data`.
 */
export const bulkErrorMessage = (error: any, fallback: string): string =>
  error?.data?.message ?? error?.response?.data?.message ?? fallback;

/**
 * Makes every bulk call unique for the duplicate-request guard in
 * axiosConfig: that guard never forgets a request with a body, so the same
 * call made twice (the modal opened again on the same selection) would be
 * dropped. The backend strips the field.
 */
const withRequestId = <T extends object>(body: T) => ({
  ...body,
  requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
});

export interface IVoucherUsersPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage?: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  hasPrevPage?: boolean;
}

interface IStore {
  /** State */
  activeVouchers: number | null;
  allVouchers: IVoucher[];
  oneVoucher: IVoucher | null;
  addedVoucher: any | null;
  updatedVoucher: any | null;
  deletedVoucher: any | null;
  hasNextPage: boolean;
  voucherUsers: IVoucherUser[];
  voucherUsersPagination: IVoucherUsersPagination | null;
  pendingVouchers: IVoucher[];
  hasNextPendingPage: boolean;
  /** Callbacks */
  getPartnerActiveVouchers: (partnerId: string, lotteryId?: string) => void;
  getAllVouchers: (
    page: number,
    limit: number,
    partnerId?: string,
    challengeId?: string
  ) => void;
  getChallengeVoucherSelection: (
    partnerId: string,
    challengeId?: string
  ) => Promise<IVoucherSelection>;
  getChallengeVoucherSummary: (partnerId: string) => Promise<IVoucherSummary>;
  bulkUpdateVouchers: (body: {
    ids: string[];
    changes: IBulkVoucherChanges;
    includeIssued?: boolean;
    dryRun?: boolean;
  }) => Promise<IBulkVoucherResult>;
  bulkDeleteVouchers: (body: {
    ids: string[];
    dryRun?: boolean;
  }) => Promise<IBulkDeleteResult>;
  getPendingVouchers: (
    page: number,
    limit: number,
    partnerId?: string,
    /** Also fetch already-coded vouchers, so one can be found and edited. */
    includeAssigned?: boolean
  ) => void;
  assignVoucherCode: (
    id: string,
    code: string,
    expireDate: string,
    /** Actual value of the partner code in EUROS — may differ from the value
     *  the store employee indicated with the drop. Optional: leaving it empty
     *  clears the value (breakfast vouchers carry no amount). */
    value: number | undefined,
    instructionsEn?: string,
    instructionsNl?: string,
    /** Short bilingual label the app shows in the voucher list, e.g.
     *  "Discount voucher" / "Ontbijtvoucher" — how a discount voucher is
     *  told apart from a breakfast one. Shown in the user's app language. */
    prizeNameEn?: string,
    prizeNameNl?: string,
    /** Correction of the drop-time rejected-items count. */
    numberOfRejected?: number
  ) => Promise<IVoucher | undefined>;
  getOneVoucher: (id: string) => void;
  getVoucherUsers: (id: string, page: number, limit: number, append?: boolean) => void;
  addVoucher: (voucher: IVoucher) => void;
  addManyVoucher: (vouchers: IVoucher[]) => Promise<IVoucher[] | undefined>;
  updateVoucher: (id: string, voucher: IVoucher) => void;
  deleteVoucher: (id: string) => void;
  clearAddedVoucher: () => void;
  clearUpdatedVoucher: () => void;
  clearDeletedVoucher: () => void;
  clearPartnerActiveVouchers: () => void;
  clearVoucherUsers: () => void;
}

export const useVouchers = create<IStore>()((set) => ({
  /** State */
  activeVouchers: null,
  allVouchers: [],
  oneVoucher: null,
  addedVoucher: null,
  updatedVoucher: null,
  deletedVoucher: null,
  hasNextPage: false,
  voucherUsers: [],
  voucherUsersPagination: null,
  pendingVouchers: [],
  hasNextPendingPage: false,
  /** Callbacks */
  getPartnerActiveVouchers: async (partnerId: string, lotteryId?: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const params = lotteryId ? `?lotteryId=${lotteryId}` : "";
      const response = await axios.get(`partners/vouchers/active/${partnerId}${params}`);
      if (typeof response?.data === "number") {
        set({ activeVouchers: response.data });
        if (response?.data === 0) {
          useAlert.getState().setAlert({
            isOpen: true,
            message:
              "No active vouchers found. Make sure you add vouchers for this Partner.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      LOG.error("Error on fetching active Vouchers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllVouchers: async (page, limit, partnerId, challengeId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const partnerParam =
        typeof partnerId === "string" && partnerId.trim()
          ? `&partnerId=${encodeURIComponent(partnerId.trim())}`
          : "";
      const challengeParam =
        typeof challengeId === "string" && challengeId.trim()
          ? `&challenge=${encodeURIComponent(challengeId.trim())}`
          : "";
      const response = await axios.get(
        `partners/vouchers?page=${page}&limit=${limit}${partnerParam}${challengeParam}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          allVouchers:
            page === 1
              ? response?.data
              : [...state.allVouchers, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching all Vouchers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  // The bulk calls re-throw, so the screen can show the backend's message and
  // never reports a change that did not happen.
  getChallengeVoucherSelection: async (partnerId, challengeId) => {
    const challengeParam = challengeId
      ? `&challenge=${encodeURIComponent(challengeId)}`
      : "";
    const response = await axios.get(
      `partners/vouchers/bulk/selection?partnerId=${encodeURIComponent(partnerId)}${challengeParam}`
    );
    return response.data;
  },
  getChallengeVoucherSummary: async (partnerId) => {
    const response = await axios.get(
      `partners/vouchers/bulk/summary?partnerId=${encodeURIComponent(partnerId)}`
    );
    return response.data;
  },
  bulkUpdateVouchers: async (body) => {
    try {
      const response = await axios.patch(
        "partners/vouchers/bulk",
        withRequestId(body)
      );
      if (!response) throw new Error("Bulk voucher request was dropped");
      return response.data;
    } catch (error) {
      LOG.error("Error on bulk updating Vouchers", { error });
      throw error;
    }
  },
  bulkDeleteVouchers: async (body) => {
    try {
      const response = await axios.post(
        "partners/vouchers/bulk/delete",
        withRequestId(body)
      );
      if (!response) throw new Error("Bulk voucher request was dropped");
      return response.data;
    } catch (error) {
      LOG.error("Error on bulk deleting Vouchers", { error });
      throw error;
    }
  },
  getPendingVouchers: async (page, limit, partnerId, includeAssigned) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const partnerParam =
        typeof partnerId === "string" && partnerId.trim()
          ? `&partnerId=${encodeURIComponent(partnerId.trim())}`
          : "";
      const includeAssignedParam = includeAssigned ? "&includeAssigned=true" : "";
      const response = await axios.get(
        `partners/vouchers/pending-codes?page=${page}&limit=${limit}${partnerParam}${includeAssignedParam}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPendingPage: response?.data?.length === limit,
          pendingVouchers:
            page === 1
              ? response?.data
              : [...state.pendingVouchers, ...response?.data],
        }));
      }
    } catch (error) {
      // Without this, a failed fetch left pendingVouchers untouched (still
      // []), which the screen renders identically to a genuinely empty
      // queue — an admin has no way to tell "nothing to do" from "couldn't load".
      LOG.error("Error on fetching pending voucher codes", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message: "Could not load pending voucher codes. Please try again.",
        severity: "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  // Per-row save from the pending-codes table: deliberately does NOT toggle
  // the global useAppLoading overlay — the row shows its own saving state.
  // Errors are re-thrown so the screen can surface the backend message
  // (e.g. "code already assigned to another voucher of this partner").
  assignVoucherCode: async (
    id,
    code,
    expireDate,
    value,
    instructionsEn,
    instructionsNl,
    prizeNameEn,
    prizeNameNl,
    numberOfRejected
  ) => {
    try {
      const response = await axios.post(
        `partners/vouchers/${id}/assign-code`,
        { code, expireDate, value, instructionsEn, instructionsNl, prizeNameEn, prizeNameNl, numberOfRejected }
      );
      if (response?.data) {
        // Update in place rather than removing from pendingVouchers — this
        // same action now also handles editing an already-assigned voucher
        // (found via includeAssigned), which must stay visible after a save,
        // not vanish. The screen re-fetches the pending-only list itself
        // after a first-time assignment if it wants the row to disappear.
        set((state) => ({
          pendingVouchers: state.pendingVouchers.map((voucher) =>
            voucher._id === id ? response.data : voucher
          ),
          allVouchers: state.allVouchers.map((voucher) =>
            voucher._id === id ? response.data : voucher
          ),
        }));
      }
      return response?.data;
    } catch (error) {
      LOG.error("Error on assigning voucher code", { error });
      throw error;
    }
  },
  getOneVoucher: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`partners/vouchers/${id}`);
      if (response?.data) {
        set({ oneVoucher: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Voucher", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getVoucherUsers: async (id: string, page = 1, limit = 10, append = false) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `partners/vouchers/details/${id}?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        // Response structure: { voucher: {...}, redeemedByUsers: [...], pagination: {...} }
        const responseData = response.data;
        const voucher = responseData.voucher || responseData;
        const users = Array.isArray(responseData.redeemedByUsers)
          ? responseData.redeemedByUsers
          : [];
        const pagination = responseData.pagination || null;

        set((state) => {
          const updatedUsers = append
            ? [...state.voucherUsers, ...users]
            : users;

          // Map pagination from backend format to our interface format
          const mappedPagination = pagination
            ? {
              currentPage: pagination.currentPage || page,
              totalPages: pagination.totalPages || 1,
              totalItems: pagination.totalItems || updatedUsers.length,
              itemsPerPage: pagination.itemsPerPage,
              hasNextPage: pagination.hasNextPage ?? false,
              hasPreviousPage: pagination.hasPreviousPage ?? pagination.hasPrevPage ?? false,
              hasPrevPage: pagination.hasPrevPage,
            }
            : null;

          return {
            // Store voucher details in oneVoucher state
            oneVoucher: voucher,
            // Store users from redeemedByUsers array
            voucherUsers: updatedUsers,
            // Store pagination object
            voucherUsersPagination: mappedPagination,
          };
        });
      }
    } catch (error) {
      LOG.error("Error on fetching voucher details", { error });
      set({ voucherUsers: [], voucherUsersPagination: null, oneVoucher: null });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addVoucher: async (partner) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("partners/vouchers", partner);
      if (response?.data) {
        set({
          allVouchers: [response.data, ...useVouchers.getState().allVouchers],
          addedVoucher: response.data?._id,
        });
      }
      return response?.data;
    } catch (error) {
      LOG.error("Error on adding Voucher", { error });
      throw error;
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addManyVoucher: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("partners/vouchers/many", body);
      if (response?.data) {
        const newVouchers = response.data;
        set((state) => ({
          allVouchers: [...newVouchers, ...state.allVouchers],
          addedVoucher: newVouchers?.[0]._id,
        }));
      }
      return response?.data;
    } catch (error) {
      LOG.error("Error on adding new Vouchers", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateVoucher: async (id, partner) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`partners/vouchers/${id}`, partner);
      if (response?.data) {
        set((state) => ({
          updatedVoucher: response.data,
          allVouchers: state.allVouchers.map((voucher) =>
            voucher._id === id ? response.data : voucher
          ),
        }));
      }
      return response?.data;
    } catch (error) {
      LOG.error("Error on updating Voucher", { error });
      throw error;
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteVoucher: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`partners/vouchers/${id}`);
      if (response?.data) {
        set((state) => ({
          deletedVoucher: response.data,
          allVouchers: state.allVouchers.filter(
            (voucher) => voucher._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting Voucher", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedVoucher: () => {
    set({ addedVoucher: null });
  },
  clearUpdatedVoucher: () => {
    set({ updatedVoucher: null });
  },
  clearDeletedVoucher: () => {
    set({ deletedVoucher: null });
  },
  clearPartnerActiveVouchers: () => {
    set({ activeVouchers: null });
  },
  clearVoucherUsers: () => {
    set({ voucherUsers: [], voucherUsersPagination: null });
  },
}));
